import PropTypes from 'prop-types';
import { Container } from 'semantic-ui-react';
import RelatedItemsDisplay from '../Helpers/RelatedItemsDisplay';
import Sections from '../Helpers/Sections';
import DefaultViewWithoutTitle from '../../Custom/DefaultViewWithoutTitle';
import { toDisplayFileUrl } from '../../../helpers/fileUrls';
import { normalizePerson, vocabTitle } from '../../../helpers/content';
import { flattenRichTextToAppURL } from '../../../helpers/richText';
import { formatDate } from '../../../helpers/dates';

const JobView = (props) => {
  const { content } = props;
  const positionType = content.job_position_type;
  const weekHours = content.job_week_hours;
  const payment = content.job_payment;
  const startDate = content.job_start_date;
  const applicationEndDate = content.job_application_end_date;
  const jobDescription = content.job_description;
  const jobPdfFile = content.job_pdf_file;
  const jobLinkedin = content.job_linkedin_link;
  const jobAdditionalLink = content.job_additional_link;

  const contacts = content.related_contacts
    ?.map(normalizePerson)
    .filter(Boolean);

  const positionTypeLabel = vocabTitle(positionType) || null;

  const hasMetaLeft = weekHours || payment;
  const hasMetaRight = startDate || applicationEndDate;
  const hasMeta = hasMetaLeft || hasMetaRight;

  const hasDescription =
    jobDescription?.data && jobDescription.data !== '<p></p>';

  const hasLinks = jobPdfFile || jobLinkedin || jobAdditionalLink;

  const hasRelations = !content.hide_related_contacts && contacts?.length > 0;

  const hasProjects =
    content.related_projects?.length > 0 && !content.hide_related_projects;

  const hasTopics =
    content.related_topics?.length > 0 && !content.hide_related_topics;

  return (
    <Container id="view-wrapper" className="JobView">
      {/* Header */}
      <div className="view-header">
        {positionTypeLabel && (
          <p className="view-header__subtitle">{positionTypeLabel}</p>
        )}
        <h2 className="view-header__title">{content.title || content.id}</h2>
        <div className="view-header__divider" />
      </div>
      <Sections>
        {/* 2-Column Metadata */}
        {hasMeta && (
          <div className="view-meta-grid">
            <div className="view-meta-grid__col">
              {weekHours && (
                <div className="view-section">
                  <h3 className="view-section__label">Weekly Hours:</h3>
                  <p className="view-section__value">{weekHours}</p>
                </div>
              )}
              {payment && (
                <div className="view-section">
                  <h3 className="view-section__label">Payment:</h3>
                  <p className="view-section__value">{payment}</p>
                </div>
              )}
            </div>

            <div className="view-meta-grid__col">
              {startDate && (
                <div className="view-section">
                  <h3 className="view-section__label">Start:</h3>
                  <p className="view-section__value">{formatDate(startDate)}</p>
                </div>
              )}
              {applicationEndDate && (
                <div className="view-section">
                  <h3 className="view-section__label">Apply by:</h3>
                  <p className="view-section__value">
                    {formatDate(applicationEndDate)}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Job Description */}
        {hasDescription && (
          <>
            <div className="view-section">
              <div
                className="view-section__richtext"
                dangerouslySetInnerHTML={{
                  __html: flattenRichTextToAppURL(jobDescription.data),
                }}
              />
            </div>
          </>
        )}

        {/* Links */}
        {hasLinks && (
          <>
            <div className="view-section">
              <div className="view-section__buttons">
                {jobPdfFile && (
                  <a
                    href={toDisplayFileUrl(jobPdfFile)}
                    className="view-section__button"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    PDF
                  </a>
                )}
                {jobLinkedin && (
                  <a
                    href={jobLinkedin}
                    className="view-section__button"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    LinkedIn
                  </a>
                )}
                {jobAdditionalLink && (
                  <a
                    href={jobAdditionalLink}
                    className="view-section__button"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    More Information
                  </a>
                )}
              </div>
            </div>
          </>
        )}

        {/* Contact */}
        {hasRelations && (
          <>
            <div className="view-section">
              <h3 className="view-section__label">Contact:</h3>
              <RelatedItemsDisplay
                items={contacts}
                mode={content.related_contacts_display}
                kind="person"
                fieldKey={`${content['@id']}-related_contacts`}
              />
            </div>
          </>
        )}

        {/* Related Projects */}
        {hasProjects && (
          <>
            <div className="view-section">
              <h3 className="view-section__label">Related Projects:</h3>
              <RelatedItemsDisplay
                items={content.related_projects}
                mode={content.related_projects_display}
                kind="plain"
                fieldKey={`${content['@id']}-related_projects`}
              />
            </div>
          </>
        )}

        {/* Related Topics */}
        {hasTopics && (
          <>
            <div className="view-section">
              <h3 className="view-section__label">Related Topics:</h3>
              <RelatedItemsDisplay
                items={content.related_topics}
                mode={content.related_topics_display}
                kind="plain"
                fieldKey={`${content['@id']}-related_topics`}
              />
            </div>
          </>
        )}
      </Sections>

      {/* The block-rendered part of the page */}
      <DefaultViewWithoutTitle {...props} />
    </Container>
  );
};

JobView.propTypes = {
  content: PropTypes.shape({
    '@id': PropTypes.string,
    title: PropTypes.string,
    id: PropTypes.string,
    hide_related_contacts: PropTypes.bool,
    related_contacts_display: PropTypes.string,
    related_contacts: PropTypes.arrayOf(
      PropTypes.shape({
        '@id': PropTypes.string,
        '@type': PropTypes.string,
        review_state: PropTypes.string,
        is_linkable: PropTypes.bool,
        full_name: PropTypes.string,
        title: PropTypes.string,
      }),
    ),
    hide_related_projects: PropTypes.bool,
    related_projects_display: PropTypes.string,
    related_projects: PropTypes.arrayOf(
      PropTypes.shape({ '@id': PropTypes.string, title: PropTypes.string }),
    ),
    hide_related_topics: PropTypes.bool,
    related_topics_display: PropTypes.string,
    related_topics: PropTypes.arrayOf(
      PropTypes.shape({ '@id': PropTypes.string, title: PropTypes.string }),
    ),
  }).isRequired,
};

export default JobView;
